import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, KanbanSquare, Bell, Upload,
  Settings as SettingsIcon, UserCircle, Menu, X, Sparkles, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { useBranding } from "../lib/branding";
import WhatsNewModal from "./WhatsNewModal";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/leads", label: "Leads", icon: KanbanSquare },
  { to: "/followups", label: "Follow-ups", icon: Bell, badge: true },
  { to: "/import", label: "Import", icon: Upload, adminOnly: true },
  { to: "/profile", label: "Profile", icon: UserCircle },
  { to: "/settings", label: "Settings", icon: SettingsIcon, adminOnly: true },
];

const G = { background: "linear-gradient(135deg, #6C47FF 0%, #E91E8C 100%)" };
const SIDEBAR = "#0F0E18";

function NavItem({ item, badge, onClick, collapsed }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-[10px] text-[13px] font-[600] transition-all select-none ${
          collapsed ? "justify-center px-0 py-[10px]" : "px-3 py-[10px]"
        } ${
          isActive
            ? "text-white shadow-[0_2px_12px_rgba(108,71,255,0.35)]"
            : "text-white/50 hover:text-white/85 hover:bg-white/[0.07]"
        }`
      }
      style={({ isActive }) => isActive ? G : {}}
    >
      <div className="relative flex-shrink-0">
        <item.icon className="h-[17px] w-[17px]" strokeWidth={2} />
        {item.badge && badge > 0 && collapsed && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-[800] w-[13px] h-[13px]">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && badge > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-[800] px-[5px] py-[1px] min-w-[17px]"
              data-testid="followup-badge">{badge}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const [badge, setBadge] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true"
  );

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", collapsed);
  }, [collapsed]);

  useEffect(() => {
    const fetch = async () => {
      try { const { data } = await api.get("/reminders/buckets"); setBadge(data.badge_count || 0); } catch {}
    };
    fetch();
    const iv = setInterval(fetch, 60000);
    return () => clearInterval(iv);
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };
  const initial = (user?.name || user?.email || "A")[0].toUpperCase();

  const Sidebar = ({ onNavClick, showCollapse }) => (
    <div className="flex flex-col h-full relative" style={{ backgroundColor: SIDEBAR }}>
      {/* Logo */}
      <div
        className="h-[66px] flex items-center flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", padding: collapsed && showCollapse ? "0 11px" : "0 20px" }}
      >
        {collapsed && showCollapse ? (
          <div className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-white font-[900] text-[15px] flex-shrink-0" style={G}>
            {branding.logo_data
              ? <img src={branding.logo_data} alt="" className="h-5 w-5 object-contain" />
              : "M"}
          </div>
        ) : branding.logo_data ? (
          <img src={branding.logo_data} alt={branding.name} className="h-8 w-auto max-w-[160px] object-contain" data-testid="brand-logo-image" />
        ) : (
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <div className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-white font-[900] text-[15px] flex-shrink-0" style={G}>
              M
            </div>
            <div>
              <div className="text-[12px] font-[800] text-white uppercase tracking-[0.06em] leading-tight">{branding.name}</div>
              <div className="text-[9px] text-white/35 tracking-[0.18em] uppercase mt-[2px]">Learning Hub</div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 space-y-[2px] overflow-y-auto" style={{ padding: collapsed && showCollapse ? "16px 8px" : "16px 12px" }}>
        {!collapsed && showCollapse && (
          <div className="text-[9px] font-[700] text-white/25 uppercase tracking-[0.18em] px-3 mb-3">Main Menu</div>
        )}
        {NAV.filter((item) => !item.adminOnly || user?.role === "admin").map((item) => (
          <NavItem key={item.to} item={item} badge={badge} onClick={onNavClick} collapsed={collapsed && showCollapse} />
        ))}
      </nav>

      {/* Bottom */}
      <div
        className="pb-5 flex-shrink-0"
        style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: collapsed && showCollapse ? "12px 8px 20px" : "0 12px 20px" }}
      >
        {/* What's New */}
        {!collapsed || !showCollapse ? (
          <div className="pt-3 mb-1">
            <button
              onClick={() => { setWhatsNewOpen(true); onNavClick?.(); }}
              data-testid="whats-new-btn"
              className="flex items-center gap-3 w-full px-3 py-[10px] rounded-[10px] text-[13px] font-[600] text-white/40 hover:text-white/80 hover:bg-white/[0.07] transition-all"
            >
              <Sparkles className="h-[17px] w-[17px] flex-shrink-0" strokeWidth={2} />
              <span>What's New</span>
            </button>
          </div>
        ) : (
          <div className="mb-1 pt-3 flex justify-center">
            <button
              onClick={() => { setWhatsNewOpen(true); onNavClick?.(); }}
              title="What's New"
              className="flex items-center justify-center p-[10px] rounded-[10px] text-white/40 hover:text-white/80 hover:bg-white/[0.07] transition-all"
            >
              <Sparkles className="h-[17px] w-[17px]" strokeWidth={2} />
            </button>
          </div>
        )}

        {/* User row */}
        {collapsed && showCollapse ? (
          <div className="flex flex-col items-center gap-1 py-2">
            <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-white text-[12px] font-[800] flex-shrink-0" style={G}>
              {initial}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              title="Sign out"
              className="text-white/30 hover:text-white/70 transition-colors p-1.5 rounded-[7px] hover:bg-white/[0.07]"
            >
              <LogOut className="h-[15px] w-[15px]" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-white text-[12px] font-[800] flex-shrink-0" style={G}>
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-[600] text-white/60 truncate" data-testid="user-email">{user?.email}</div>
              <div className="text-[10px] text-white/30 capitalize">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              title="Sign out"
              className="text-white/30 hover:text-white/70 transition-colors p-1.5 rounded-[7px] hover:bg-white/[0.07]"
            >
              <LogOut className="h-[15px] w-[15px]" />
            </button>
          </div>
        )}

        {/* Collapse toggle */}
        {showCollapse && (
          <div className={`mt-2 ${collapsed ? "flex justify-center" : ""}`}>
            <button
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="flex items-center gap-2 w-full rounded-[10px] text-[11px] font-[600] text-white/25 hover:text-white/60 hover:bg-white/[0.07] transition-all"
              style={{ padding: collapsed ? "8px 0" : "8px 12px", justifyContent: collapsed ? "center" : "flex-start" }}
            >
              {collapsed
                ? <ChevronRight className="h-[15px] w-[15px]" />
                : <><ChevronLeft className="h-[15px] w-[15px]" /><span>Collapse</span></>
              }
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: "#F0F1F8" }}>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col flex-shrink-0 sticky top-0 h-screen overflow-hidden"
        style={{
          width: collapsed ? 56 : 232,
          transition: "width 0.22s cubic-bezier(0.4,0,0.2,1)",
        }}
        data-testid="sidebar"
      >
        <Sidebar showCollapse />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-[232px] h-full overflow-hidden shadow-2xl">
            <Sidebar onNavClick={() => setMobileOpen(false)} />
          </aside>
          <button
            className="absolute top-4 right-4 text-white p-2 rounded-full bg-white/10"
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center gap-3 px-4 h-[58px] bg-white border-b border-[rgba(0,0,0,0.07)] flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-[8px] hover:bg-[#F0F1F8] text-[#0D0B1F]"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-[7px] flex items-center justify-center text-white font-[900] text-[12px]" style={G}>M</div>
            <span className="text-[13px] font-[800] text-[#0D0B1F] uppercase tracking-wide">{branding.name}</span>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1440px] mx-auto px-6 py-7 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      <WhatsNewModal open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
    </div>
  );
}
